// Per-user data-entry capabilities that an ADMIN can delegate to board directors.
// ADMIN implicitly has all of these; other roles only have what's granted.
// Keys are stored in User.permissions (string[]) and checked by hasCapability().

export const CAPABILITIES = [
  "onbehalf:works_permits",
  "onbehalf:maintenance",
  "onbehalf:incidents",
  "finance:manage",
] as const;

export type Capability = (typeof CAPABILITIES)[number];

export function isCapability(value: string): value is Capability {
  return (CAPABILITIES as readonly string[]).includes(value);
}

// Bilingual labels for the admin permission checkboxes.
export const CAPABILITY_LABELS: Record<Capability, { es: string; en: string }> = {
  "onbehalf:works_permits": {
    es: "Registrar permisos de obra en nombre de residentes",
    en: "Enter works permits on behalf of residents",
  },
  "onbehalf:maintenance": {
    es: "Registrar solicitudes de mantenimiento en nombre de residentes",
    en: "Enter maintenance requests on behalf of residents",
  },
  "onbehalf:incidents": {
    es: "Registrar incidentes en nombre de residentes",
    en: "Enter incidents on behalf of residents",
  },
  "finance:manage": {
    es: "Gestionar finanzas (contable): cuotas, gastos, informes y cierre mensual",
    en: "Manage finances (bookkeeper): fees, expenses, reports and monthly close",
  },
};

// Role+permissions check usable on both client and server. ADMIN implicitly
// has every capability; the board Treasurer implicitly manages finances (the
// seat carries the duty, so it never has to be granted by hand); other roles
// need the key in User.permissions.
export function userHasCapability(
  user: { role: string; permissions?: string[] | null; boardPosition?: string | null } | null | undefined,
  capability: Capability | string
): boolean {
  if (!user) return false;
  if (user.role === "ADMIN") return true;
  if (capability === "finance:manage" && user.boardPosition === "TREASURER") return true;
  return Array.isArray(user.permissions) && user.permissions.includes(capability);
}

/**
 * Who may convene a meeting: the administrator and the board Secretary only.
 * Per the Reglamento the Secretaria calls meetings and issues the notices; the
 * rest of the Junta participates but does not schedule. Shared by the client
 * (to hide the controls) and the server (the real gate — see
 * `authorizeMeetingOrganizer`).
 */
export function canOrganizeMeetings(
  user: { role: string; boardPosition?: string | null } | null | undefined
): boolean {
  if (!user) return false;
  return user.role === "ADMIN" || user.boardPosition === "SECRETARY";
}
